/**
 * „Geteilte Anlagen" im Profil (#47): offene Einladungen an die eigene Adresse
 * mit „Annehmen" und „Ablehnen", darunter die Anlagen, die schon mit mir
 * geteilt sind.
 *
 * **Die Einladung steht hier, weil die Nachricht nicht ankommen muss.** Bis
 * dahin führte nur der Link aus der E-Mail zur Annahme; lag sie im Spam,
 * gab es keinen Weg. Angenommen wird über denselben Ablauf wie mit dem Link —
 * die Adresse des Kontos ist die Berechtigung, nicht der Besitz der Nachricht.
 *
 * **Beide Arten stehen in einer Liste**, die Wohnung und die geteilte Anlage:
 * Wer eingeladen ist, fragt „wer will mir etwas zeigen", nicht „welche Art
 * Zugang ist das". Die Art steht als Marke dabei.
 *
 * **Ablehnen und Beenden fragen nach** — beides lässt sich nur durch eine neue
 * Einladung des Besitzers rückgängig machen. Annehmen fragt nicht: Es lässt sich
 * hier unten wieder beenden.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Dialog } from "@hausmeter/ui";
import { useState, type ReactNode } from "react";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { formatDate, formatDateTime } from "../lib/format.js";
import { useMyInvitations, useSharedSites } from "../lib/queries.js";
import { navigate, routeFor } from "../lib/router.js";
import { shareAccessLabel, sharedSiteLabel } from "../lib/site-access.js";

type MyInvitation = ApiComponents["schemas"]["MyInvitation"];
type SharedSite = ApiComponents["schemas"]["SharedSite"];

/** Was hinter einer Einladung steht — in einer Zeile unter dem Namen. */
export function invitationFacts(invitation: MyInvitation): string {
  const facts: string[] = [];
  if (invitation.kind === "unit") {
    if (invitation.unitName !== null) facts.push(invitation.unitName);
    if (invitation.movedInOn !== null) {
      facts.push(
        invitation.movedOutOn === null
          ? `ab ${formatDate(invitation.movedInOn)}`
          : `${formatDate(invitation.movedInOn)} bis ${formatDate(invitation.movedOutOn)}`,
      );
    }
  } else if (invitation.shareAccess !== null) {
    facts.push(shareAccessLabel(invitation.shareAccess));
  }
  if (invitation.invitedBy !== null) facts.push(`von ${invitation.invitedBy}`);
  facts.push(`gültig bis ${formatDateTime(invitation.expiresAt)}`);
  return facts.join(" · ");
}

export function SharedSitesCard() {
  const queryClient = useQueryClient();
  const invitations = useMyInvitations();
  const shared = useSharedSites();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; siteId: string | null } | null>(null);
  const [declining, setDeclining] = useState<MyInvitation | null>(null);
  const [ending, setEnding] = useState<SharedSite | null>(null);

  // Einladungen, geteilte Anlagen und Wohnungen hängen am Nutzer und ändern sich zusammen.
  const reload = () => queryClient.invalidateQueries({ queryKey: ["me"] });

  const accept = useMutation({
    mutationFn: async (invitation: MyInvitation) => {
      const { data, error: failed } = await api.POST("/me/invitations/{invitationId}/accept", {
        params: { path: { invitationId: invitation.id } },
      });
      if (failed || !data) throw new Error(apiErrorMessage(failed) ?? "Die Einladung ließ sich nicht annehmen.");
      return data;
    },
    onSuccess: async (accepted, invitation) => {
      setError(null);
      setNotice(
        accepted.kind === "site_share"
          ? { text: `„${invitation.siteName}" steht jetzt oben in der Auswahl.`, siteId: accepted.siteId }
          : { text: `${invitation.unitName ?? "Die Wohnung"} steht jetzt oben in der Auswahl.`, siteId: null },
      );
      await reload();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const decline = useMutation({
    mutationFn: async (invitation: MyInvitation) => {
      const { error: failed } = await api.POST("/me/invitations/{invitationId}/decline", {
        params: { path: { invitationId: invitation.id } },
      });
      if (failed) throw new Error(apiErrorMessage(failed) ?? "Die Einladung ließ sich nicht ablehnen.");
    },
    onSuccess: async () => {
      setError(null);
      setNotice(null);
      setDeclining(null);
      await reload();
    },
    onError: (cause: Error) => {
      setDeclining(null);
      setError(cause.message);
    },
  });

  const end = useMutation({
    mutationFn: async (entry: SharedSite) => {
      const { error: failed } = await api.DELETE("/site-shares/{shareId}", {
        params: { path: { shareId: entry.shareId } },
      });
      if (failed) throw new Error(apiErrorMessage(failed) ?? "Die Teilung ließ sich nicht beenden.");
    },
    onSuccess: async () => {
      setError(null);
      setNotice(null);
      setEnding(null);
      await reload();
    },
    onError: (cause: Error) => {
      setEnding(null);
      setError(cause.message);
    },
  });

  // Eine Handlung zur Zeit: Ein zweiter Klick ersetzte sonst `declining`/`ending`, und die Antwort der ersten träfe die falsche Zeile.
  const busy = accept.isPending || decline.isPending || end.isPending;
  const open = invitations.data?.invitations ?? [];
  const sites = shared.data?.sites ?? [];
  const loading = invitations.isPending || shared.isPending;

  return (
    <Card title="Geteilte Anlagen" padding="lg">
      {loading && <p style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>Wird geladen …</p>}
      {invitations.isError && <Alert>{invitations.error.message}</Alert>}
      {shared.isError && <Alert>{shared.error.message}</Alert>}

      {!loading && open.length === 0 && sites.length === 0 && (
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--text-muted)" }}>
          Mit dir ist keine Anlage geteilt, und es liegt keine Einladung vor. Teilt jemand eine Anlage mit dir,
          erscheint die Einladung hier und als E-Mail.
        </p>
      )}

      {open.length > 0 && (
        <>
          <Heading>Einladungen</Heading>
          {open.map((invitation) => (
            <Row
              key={invitation.id}
              title={invitation.siteName}
              badge={invitation.kind === "unit" ? "Wohnung" : "Anlage"}
              facts={invitationFacts(invitation)}
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setDeclining(invitation)}
              >
                Ablehnen …
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => accept.mutate(invitation)}
              >
                {accept.isPending && accept.variables?.id === invitation.id ? "Wird angenommen …" : "Annehmen"}
              </Button>
            </Row>
          ))}
        </>
      )}

      {sites.length > 0 && (
        <>
          <Heading>Mit mir geteilt</Heading>
          {sites.map((entry) => (
            <Row key={entry.shareId} title={entry.site.name} facts={sharedSiteLabel(entry)}>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEnding(entry)}>
                Beenden …
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(routeFor("electricity", entry.site.id))}
              >
                Öffnen
              </Button>
            </Row>
          ))}
        </>
      )}

      {notice !== null && (
        <div style={{ marginTop: 12 }}>
          <Alert
            tone="info"
            action={
              notice.siteId !== null ? (
                <Button variant="secondary" size="sm" onClick={() => navigate(routeFor("electricity", notice.siteId))}>
                  Öffnen
                </Button>
              ) : undefined
            }
          >
            {notice.text}
          </Alert>
        </div>
      )}
      {error !== null && (
        <div style={{ marginTop: 12 }}>
          <Alert>{error}</Alert>
        </div>
      )}

      <Dialog
        open={declining !== null}
        onClose={() => setDeclining(null)}
        title={declining === null ? "Einladung ablehnen?" : `Einladung zu „${declining.siteName}" ablehnen?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeclining(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              disabled={decline.isPending}
              onClick={() => declining !== null && decline.mutate(declining)}
            >
              Ablehnen
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>
          Der Link in der E-Mail gilt danach nicht mehr. {declining?.invitedBy ?? "Wer eingeladen hat"} sieht, dass du
          abgelehnt hast, und kann dich neu einladen.
        </p>
      </Dialog>

      <Dialog
        open={ending !== null}
        onClose={() => setEnding(null)}
        title={ending === null ? "Teilung beenden?" : `„${ending.site.name}" nicht mehr sehen?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEnding(null)}>
              Abbrechen
            </Button>
            <Button variant="danger" disabled={end.isPending} onClick={() => ending !== null && end.mutate(ending)}>
              Beenden
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>
          Die Anlage verschwindet aus deiner Auswahl. Sehen kannst du sie erst wieder, wenn ihr Besitzer dich neu einlädt.
        </p>
      </Dialog>
    </Card>
  );
}

function Heading({ children }: { readonly children: string }) {
  return (
    <div
      style={{
        margin: "12px 0 4px",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "var(--text-muted)",
      }}
    >
      {children}
    </div>
  );
}

function Row({
  title,
  badge,
  facts,
  children,
}: {
  readonly title: string;
  readonly badge?: string;
  readonly facts: string;
  readonly children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 0",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <div style={{ minWidth: 0, flex: "1 1 240px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)", overflowWrap: "anywhere" }}>
            {title}
          </span>
          {badge !== undefined && <Badge>{badge}</Badge>}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, overflowWrap: "anywhere" }}>{facts}</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>{children}</div>
    </div>
  );
}
